(() => {
  const banner = document.querySelector('[data-yx-cookie-consent]');
  const preferencesDialog = document.querySelector('[data-yx-cookie-preferences]');

  if (!banner || !preferencesDialog) return;

  const form = preferencesDialog.querySelector('[data-yx-cookie-form]');
  const bannerStatus = banner.querySelector('[data-yx-cookie-status]');
  const preferencesStatus = preferencesDialog.querySelector('[data-yx-cookie-preferences-status]');
  const inputs = Object.fromEntries(
    [...preferencesDialog.querySelectorAll('[data-yx-cookie-category]')].map((input) => [
      input.dataset.yxCookieCategory,
      input,
    ])
  );
  let privacyApi = null;
  let privacyRetry = null;
  let privacyAttempts = 0;
  let returnFocus = null;
  let localConsent = null;
  let previewBar = null;
  let previewBarResizeObserver = null;
  const localConsentStorageKey = 'yx-cookie-consent-v1';
  const isLocalhost = ['127.0.0.1', 'localhost'].includes(window.location.hostname);
  const isLocalPreview =
    isLocalhost &&
    new URLSearchParams(window.location.search).has('yx-cookie-consent-preview');

  const copy = document.documentElement.lang.toLowerCase().startsWith('es')
    ? {
        error: 'No se han podido guardar tus preferencias. Inténtalo de nuevo.',
      }
    : {
        error: 'We could not save your preferences. Please try again.',
      };

  const setStatus = (element, message = '') => {
    element.textContent = message;
    element.hidden = !message;
  };

  const parseConsent = (value) => value === true || value === 'yes';

  const normalizeConsent = (value) => {
    if (value === true || value === 'yes') return true;
    if (value === false || value === 'no') return false;
    return null;
  };

  const readVisitorConsent = () => {
    const api = window.Shopify?.customerPrivacy;
    return typeof api?.currentVisitorConsent === 'function' ? api.currentVisitorConsent() : null;
  };

  const readLocalConsent = () => {
    if (!isLocalhost) return null;

    try {
      const saved = JSON.parse(window.localStorage.getItem(localConsentStorageKey));
      if (!saved || !['preferences', 'analytics', 'marketing'].every(
        (category) => typeof saved[category] === 'boolean'
      )) return null;
      return saved;
    } catch (_error) {
      return null;
    }
  };

  const writeLocalConsent = (consent) => {
    try {
      window.localStorage.setItem(localConsentStorageKey, JSON.stringify(consent));
    } catch (_error) {
      // The banner still closes for this page if storage is unavailable.
    }
  };

  const consentWasSaved = (consent) => {
    const current = readVisitorConsent();
    if (!current) return false;

    return Object.entries(consent).every(
      ([category, value]) => normalizeConsent(current[category]) === value
    );
  };

  const visitorHasConsentDecision = () => {
    const current = readVisitorConsent();
    if (!current) return false;

    return ['preferences', 'analytics', 'marketing'].every(
      (category) => normalizeConsent(current[category]) !== null
    );
  };

  const syncPreviewBarOffset = () => {
    const rect = previewBar?.getBoundingClientRect();
    const overlapsViewportBottom = rect?.height > 0 && rect.bottom >= window.innerHeight - 1;
    const offset = overlapsViewportBottom ? Math.max(0, window.innerHeight - rect.top) : 0;
    banner.style.setProperty('--yx-cookie-preview-offset', `${offset}px`);
  };

  const bindPreviewBar = () => {
    const nextPreviewBar = document.getElementById('PBarNextFrameWrapper');
    if (nextPreviewBar === previewBar) return;

    previewBar?.removeEventListener('toggle', syncPreviewBarOffset);
    previewBarResizeObserver?.disconnect();
    previewBar = nextPreviewBar;

    if (previewBar) {
      previewBar.addEventListener('toggle', syncPreviewBarOffset);
      if ('ResizeObserver' in window) {
        previewBarResizeObserver = new ResizeObserver(syncPreviewBarOffset);
        previewBarResizeObserver.observe(previewBar);
      }
    }

    syncPreviewBarOffset();
  };

  const previewBarObserver = new MutationObserver(bindPreviewBar);
  previewBarObserver.observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('resize', syncPreviewBarOffset);
  bindPreviewBar();

  const syncPreferenceInputs = () => {
    const current = localConsent || readVisitorConsent();
    if (!current) return;
    inputs.preferences.checked = parseConsent(current.preferences);
    inputs.analytics.checked = parseConsent(current.analytics);
    inputs.marketing.checked = parseConsent(current.marketing);
  };

  const openPreferences = (trigger) => {
    returnFocus = trigger || document.activeElement;
    syncPreferenceInputs();
    setStatus(preferencesStatus);
    document.documentElement.classList.add('yx-cookie-preferences-open');

    if (typeof preferencesDialog.showModal === 'function') {
      if (!preferencesDialog.open) preferencesDialog.showModal();
    } else {
      preferencesDialog.setAttribute('open', '');
    }
  };

  const closePreferences = () => {
    if (typeof preferencesDialog.close === 'function' && preferencesDialog.open) {
      preferencesDialog.close();
    } else {
      preferencesDialog.removeAttribute('open');
    }
    document.documentElement.classList.remove('yx-cookie-preferences-open');
    returnFocus?.focus?.();
  };

  const saveConsent = (consent, onSuccess) => {
    setStatus(bannerStatus);
    setStatus(preferencesStatus);

    // Shopify CLI runs on 127.0.0.1, where Shopify cannot write its
    // domain-scoped consent cookie. Persist the same choice in localStorage
    // for local QA only; live storefronts continue to use Shopify's API.
    if (isLocalhost) {
      localConsent = consent;
      writeLocalConsent(consent);
      banner.hidden = true;
      onSuccess?.();
      document.dispatchEvent(new CustomEvent('yx:cookie-consent-updated', { detail: consent }));
      return;
    }

    privacyApi = window.Shopify?.customerPrivacy || null;
    if (typeof privacyApi?.setTrackingConsent !== 'function') {
      setStatus(preferencesDialog.open ? preferencesStatus : bannerStatus, copy.error);
      return;
    }

    privacyApi.setTrackingConsent(consent, (result = {}) => {
      // Shopify can return an error after its consent state has already been
      // updated. Treat the persisted API state as authoritative so one valid
      // visitor click does not leave the banner open with a false error.
      if (result.error && !consentWasSaved(consent)) {
        console.warn('[YURINOX cookie consent] Shopify could not save consent.', result.error);
        setStatus(preferencesDialog.open ? preferencesStatus : bannerStatus, copy.error);
        return;
      }

      localConsent = consent;
      banner.hidden = true;
      onSuccess?.();
      document.dispatchEvent(new CustomEvent('yx:cookie-consent-updated', { detail: consent }));
    });
  };

  banner.querySelector('[data-yx-cookie-accept]').addEventListener('click', () => {
    saveConsent({ preferences: true, analytics: true, marketing: true });
  });

  banner.querySelector('[data-yx-cookie-decline]').addEventListener('click', () => {
    saveConsent({ preferences: false, analytics: false, marketing: false });
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-yx-cookie-manage]');
    if (!trigger) return;
    event.preventDefault();
    openPreferences(trigger);
  });

  preferencesDialog.querySelector('[data-yx-cookie-close]').addEventListener('click', closePreferences);

  preferencesDialog.querySelector('[data-yx-cookie-decline-all]').addEventListener('click', () => {
    saveConsent(
      { preferences: false, analytics: false, marketing: false },
      closePreferences
    );
  });

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    saveConsent(
      {
        preferences: inputs.preferences.checked,
        analytics: inputs.analytics.checked,
        marketing: inputs.marketing.checked,
      },
      closePreferences
    );
  });

  preferencesDialog.addEventListener('click', (event) => {
    if (event.target === preferencesDialog) closePreferences();
  });

  preferencesDialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    closePreferences();
  });

  const initialize = () => {
    clearTimeout(privacyRetry);
    privacyRetry = null;
    privacyApi = window.Shopify?.customerPrivacy || null;
    bindPreviewBar();
    if (isLocalPreview) {
      banner.hidden = false;
      return;
    }
    if (isLocalhost) {
      localConsent = readLocalConsent();
      banner.hidden = Boolean(localConsent);
      return;
    }
    // Some embedded browsers expose a partial API even after loadFeatures
    // calls back. Wait briefly for the real methods; never invent consent.
    if (!['shouldShowBanner', 'currentVisitorConsent', 'setTrackingConsent'].every(
      (method) => typeof privacyApi?.[method] === 'function'
    )) {
      if (privacyAttempts++ < 20) privacyRetry = setTimeout(initialize, 250);
      return;
    }
    banner.hidden = !privacyApi.shouldShowBanner() || visitorHasConsentDecision();
  };

  window.addEventListener('load', initialize, { once: true });

  if (window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => {
        if (!error) initialize();
      }
    );
  } else if (document.readyState === 'complete') initialize();
})();
